import { memo, useCallback, useMemo } from "react";
import "@visioni/a3s-ui/theme";
import { StyleSheet } from "react-native-unistyles";
import { View, Input, Autocomplete } from "@visioni/a3s-ui/components";

export type UserListFilterRole = "tenant_admin" | "site_manager" | "site_viewer" | "camera_manager" | "camera_viewer";

export interface UserListFilterSite {
  readonly id: string;
  readonly name: string;
}

export interface UserListFilterCamera {
  readonly id: string;
  readonly name: string;
}

export interface UserListFilters {
  readonly search: string;
  readonly role: UserListFilterRole | null;
  readonly siteId: string | null;
  readonly cameraId: string | null;
}

export interface UserListFilterRowProps {
  readonly filters: UserListFilters;
  readonly onFiltersChange: (filters: UserListFilters) => void;
  readonly sites: readonly UserListFilterSite[];
  readonly cameras: readonly UserListFilterCamera[];
}

interface RoleOption {
  readonly id: UserListFilterRole;
  readonly label: string;
}

const ROLE_OPTIONS: readonly RoleOption[] = [
  { id: "tenant_admin", label: "Tenant Admin" },
  { id: "site_manager", label: "Site Manager" },
  { id: "site_viewer", label: "Site Viewer" },
  { id: "camera_manager", label: "Camera Manager" },
  { id: "camera_viewer", label: "Camera Viewer" },
];

const getRoleOptionLabel = (option: RoleOption): string => option.label;
const getRoleOptionId = (option: RoleOption): string => option.id;
const getSiteOptionLabel = (option: UserListFilterSite): string => option.name;
const getSiteOptionId = (option: UserListFilterSite): string => option.id;
const getCameraOptionLabel = (option: UserListFilterCamera): string => option.name;
const getCameraOptionId = (option: UserListFilterCamera): string => option.id;

export const UserListFilterRow = memo(({ filters, onFiltersChange, sites, cameras }: UserListFilterRowProps) => {
  const handleSearchChange = useCallback(
    (text: string) => {
      onFiltersChange({ ...filters, search: text });
    },
    [filters, onFiltersChange],
  );

  const handleRoleChange = useCallback(
    (value: RoleOption | null) => {
      onFiltersChange({ ...filters, role: value?.id ?? null });
    },
    [filters, onFiltersChange],
  );

  const handleSiteChange = useCallback(
    (value: UserListFilterSite | null) => {
      onFiltersChange({ ...filters, siteId: value?.id ?? null });
    },
    [filters, onFiltersChange],
  );

  const handleCameraChange = useCallback(
    (value: UserListFilterCamera | null) => {
      onFiltersChange({ ...filters, cameraId: value?.id ?? null });
    },
    [filters, onFiltersChange],
  );

  const selectedRole = useMemo(() => ROLE_OPTIONS.find((r) => r.id === filters.role) ?? null, [filters.role]);

  const selectedSite = useMemo(() => sites.find((s) => s.id === filters.siteId) ?? null, [sites, filters.siteId]);

  const selectedCamera = useMemo(
    () => cameras.find((c) => c.id === filters.cameraId) ?? null,
    [cameras, filters.cameraId],
  );

  return (
    <View style={styles.container}>
      <View style={[styles.searchField, { zIndex: 4 }]}>
        <Input
          value={filters.search}
          onChangeText={handleSearchChange}
          placeholder="Search by name or email"
          clearable
        />
      </View>
      <View style={[styles.selectField, { zIndex: 3 }]}>
        <Autocomplete
          options={ROLE_OPTIONS as RoleOption[]}
          value={selectedRole}
          onChange={handleRoleChange}
          getOptionLabel={getRoleOptionLabel}
          getOptionId={getRoleOptionId}
          placeholder="All roles"
          label="Role"
        />
      </View>
      <View style={[styles.selectField, { zIndex: 2 }]}>
        <Autocomplete
          options={sites as UserListFilterSite[]}
          value={selectedSite}
          onChange={handleSiteChange}
          getOptionLabel={getSiteOptionLabel}
          getOptionId={getSiteOptionId}
          placeholder="All sites"
          label="Site"
        />
      </View>
      <View style={[styles.selectField, { zIndex: 1 }]}>
        <Autocomplete
          options={cameras as UserListFilterCamera[]}
          value={selectedCamera}
          onChange={handleCameraChange}
          getOptionLabel={getCameraOptionLabel}
          getOptionId={getCameraOptionId}
          placeholder="All cameras"
          label="Camera"
        />
      </View>
    </View>
  );
});

UserListFilterRow.displayName = "UserListFilterRow";

const styles = StyleSheet.create((theme) => ({
  container: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing.lg,
  },
  searchField: {
    flexBasis: "48%",
    flexGrow: 1,
    minWidth: 160,
  },
  selectField: {
    flexBasis: "48%",
    flexGrow: 1,
    minWidth: 160,
  },
}));
